import { module as BaseModule } from "modujs"

export default class extends BaseModule {
  init() {
    this.content = this.el.querySelector("[data-read-more-content]")
    this.toggle = this.el.querySelector("[data-read-more-toggle]")
    if (!this.content || !this.toggle) return

    this.lines = parseInt(this.el.dataset.readMoreLines || "10", 10)
    this.collapsedLabel = this.el.dataset.readMoreLabelOpen || "― read more"
    this.expandedLabel = this.el.dataset.readMoreLabelClose || "― read less"
    this.debug =
      this.el.dataset.readMoreDebug === "true" ||
      this.el.hasAttribute("data-read-more-debug")
    this.isExpanded = false
    this.originalHtml = this.content.innerHTML
    this.originalText = this.content.textContent || ""

    this.handleResize = this.handleResize.bind(this)
    this.handleToggle = this.handleToggle.bind(this)

    this.toggle.addEventListener("click", this.handleToggle)
    window.addEventListener("resize", this.handleResize)
    this.handleResize()

 
  }

  handleToggle() {
    if (this.toggle.hidden) return

    this.isExpanded = !this.isExpanded
    this.applyClamp()
  }

  handleResize() {
    this.charLimit = this.getCharLimit()
    const needsClamp = this.originalText.length > this.charLimit

    if (this.debug) {
      console.debug("[ReadMore] resize", {
        lines: this.lines,
        charLimit: this.charLimit,
        textLength: this.originalText.length,
        needsClamp,
      })
    }

    this.toggle.hidden = !needsClamp
    this.el.dataset.readMoreReady = "true"

    if (!needsClamp) {
      this.isExpanded = true
      this.content.innerHTML = this.originalHtml
      this.el.dataset.readMoreExpanded = "true"
      return
    }

    if (!this.isExpanded) {
      this.applyClamp()
    }
  }

  applyClamp() {
    if (this.isExpanded) {
      this.content.innerHTML = this.originalHtml
      this.el.dataset.readMoreExpanded = "true"
      this.toggle.textContent = this.expandedLabel
    } else {
      this.content.textContent = this.getTrimmedText()
      this.el.dataset.readMoreExpanded = "false"
      this.toggle.textContent = this.collapsedLabel
    }

    if (this.debug) {
      console.debug("[ReadMore] applyClamp", {
        isExpanded: this.isExpanded,
        label: this.isExpanded ? this.expandedLabel : this.collapsedLabel,
      })
    }
  }

  getTrimmedText() {
    const limit = Math.max(0, this.charLimit || 0)
    if (this.originalText.length <= limit) return this.originalText

    const sliced = this.originalText.slice(0, limit)
    const lastSpace = sliced.lastIndexOf(" ")
    const trimmed = lastSpace > 0 ? sliced.slice(0, lastSpace) : sliced
    return `${trimmed}...`
  }

  getCharLimit() {
    const computed = window.getComputedStyle(this.content)
    const fontSize = parseFloat(computed.fontSize) || 16
    const width = this.getMeasurementWidth()

    if (!width || width <= 0) return this.originalText.length

    const avgCharWidth = this.getAverageCharWidth(computed, fontSize)
    const charsPerLine = Math.max(1, Math.floor(width / avgCharWidth))
    if (this.debug) {
      console.debug("[ReadMore] charLimit", {
        width,
        fontSize,
        avgCharWidth,
        charsPerLine,
        lines: this.lines,
      })
    }
    return charsPerLine * this.lines
  }

  getAverageCharWidth(computed, fontSize) {
    const canvas =
      this.canvas || (this.canvas = document.createElement("canvas"))
    const context = canvas.getContext("2d")

    if (!context) return fontSize * 0.55

    const fontStyle = computed.fontStyle || "normal"
    const fontVariant = computed.fontVariant || "normal"
    const fontWeight = computed.fontWeight || "normal"
    const fontFamily = computed.fontFamily || "sans-serif"

    context.font = `${fontStyle} ${fontVariant} ${fontWeight} ${fontSize}px ${fontFamily}`

    const sample =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    const metrics = context.measureText(sample)
    const avg = metrics.width / sample.length

    return avg || fontSize * 0.55
  }

  getMeasurementWidth() {
    const directWidth =
      this.content.parentElement?.getBoundingClientRect().width ||
      this.content.getBoundingClientRect().width ||
      this.el.getBoundingClientRect().width

    if (directWidth && directWidth > 0) return directWidth

    const clone = this.el.cloneNode(true)
    clone.style.position = "absolute"
    clone.style.visibility = "hidden"
    clone.style.pointerEvents = "none"
    clone.style.left = "-9999px"
    clone.style.top = "0"
    clone.style.height = "auto"
    clone.style.display = "block"

    document.body.appendChild(clone)
    const cloneContent = clone.querySelector("[data-read-more-content]")
    const width =
      cloneContent?.parentElement?.getBoundingClientRect().width ||
      cloneContent?.getBoundingClientRect().width ||
      clone.getBoundingClientRect().width
    document.body.removeChild(clone)

    if (this.debug) {
      console.debug("[ReadMore] measurementWidth", {
        directWidth,
        cloneWidth: width,
      })
    }

    return width || document.documentElement.clientWidth
  }
}
